/*
 * Convert Elastic data to various other formats.
 */
import { pathOr } from 'ramda';

class ElasticTransforms {
  constructor() {
    // TODO
  }

  normalize(elasticAggregations, timeSeries) {
    let emptyResult = {
      metrics: [],
      metricsValues: [],
      timestamps: [],
    };

    if (typeof elasticAggregations === 'undefined' || typeof timeSeries === 'undefined') {
      return emptyResult;
    }

    let timelineAggName = 'timeline';
    let timelineAggEntry = pathOr(null, [timelineAggName], elasticAggregations);

    if (!timelineAggEntry || !Array.isArray(timeSeries) || timeSeries.length <= 0) {
      return emptyResult;
    }

    let metricsHash = {};
    let metricsMap = [];
    let metricsValuesMap = [];
    let timestampsSet = new Set();

    // Set up all metrics that can be set in advance
    // TODO Optimize - create all dynamically as we go through aggregation results
    timeSeries.map((x) => {
      if ('path' in x) {
        let size = metricsMap.push(x.title);
        metricsValuesMap.push({});
        metricsHash[x.title] = size - 1;
      }
    });

    let timelineBuckets = pathOr([], ['buckets'], timelineAggEntry);

    // Go over all timeline buckets
    for (let j = 0; j < timelineBuckets.length; j++) {
      let bucketEntry = timelineBuckets[j];
      let ts = pathOr(null, ['key'], bucketEntry);
      if (ts) {
        timestampsSet.add(ts);

        // TODO Optimize overall
        // now go over all time series in this bucket
        // let metricsValuesMapIdx = 0;
        timeSeries.map((x) => {
          // Set this metric value at this timestamp
          // Depending on the type of aggregation
          if ('path' in x) {
            let idx = metricsHash[x.title]; // Don't check for null - here we can have index 0
            metricsValuesMap[idx][ts] = pathOr(null, x.path, bucketEntry);
          } else if ('buckets' in x) {
            // here we determine time series dynamically, based on values in buckets
            let allSubAggBuckets = pathOr([], x.buckets, bucketEntry);
            allSubAggBuckets.map((b) => {
              let key = pathOr(null, ['key'], b);
              let valField = x.value || 'doc_count';
              let val = pathOr(null, [valField], b);
              // value can be NaN in some aggregations, like percentile if there are no documents
              if (val === 'NaN') {
                val = null;
              }
              if (key && val) {
                let metricName = x.title + key;
                if (!(metricName in metricsHash)) {
                  let size = metricsMap.push(metricName);
                  metricsValuesMap.push({});
                  metricsHash[metricName] = size - 1;
                }
                let idx = metricsHash[metricName];
                metricsValuesMap[idx][ts] = val;
              }
            });
          }
        });
      }
    }

    // Sort timestamps in ascending order
    let timestampsList = Array.from(timestampsSet);
    timestampsList.sort(function (a, b) {
      return a - b;
    });

    // We assume ES aggregation created with elasticRequestBuilder has filled the gaps in timestamps
    return {
      metrics: metricsMap, // All found metrics
      metricsValues: metricsValuesMap, // All found values for each metric
      timestamps: timestampsList, // Sorted timestamps
    };
  }

  // TODO Reconsider name
  // Convert normalized result to data format understood by Dygraphs:
  // {
  //   labels: ["Date",<Metric Name 1>, <Metric Name 2>, ...]
  //   data: [Date1,<Metric Value 1>,<Metric Value 2>, ...]
  // }
  toDygraphs(elasticAggregations, timeSeries) {
    let result = { labels: [], data: [] };

    const { metrics, metricsValues, timestamps } = this.normalize(elasticAggregations, timeSeries);

    let resultEntries = timestamps.map((x) => {
      let dataEntry = [new Date(x)];
      metrics.map((m, idx) => {
        let mv = x in metricsValues[idx] ? metricsValues[idx][x] : null;
        dataEntry.push(mv);
      });
      return dataEntry;
    });

    result.labels = metrics;
    result.labels.unshift('Date');
    result.data = resultEntries;

    return result;
  }

  normalizeExAddGroupings(result, aggregations, groupings, path = '', key = '', record = null) {
    for (let gSpec of groupings) {
      let name = gSpec.name;
      let type = gSpec.type;
      let currentPath = path === '' ? `${name}` : `${path}.${name}`;
      let currentKey = key;
      let aggResult = pathOr(null, [name], aggregations);
      if (aggResult) {
        let aggData = [];
        switch (type) {
          case 'date_histogram':
          case 'terms': {
            let b = pathOr([], ['buckets'], aggResult);
            b.map((x) => {
              let bucketEntry = { key: x.key, path: currentPath, values: [], value: pathOr(0, ['doc_count'], x) };
              if ('groupings' in gSpec) {
                this.normalizeExAddGroupings(bucketEntry, x, gSpec.groupings, currentPath, x.key, record ? record : bucketEntry);
              }
              aggData.push(bucketEntry);
            });
            break;
          }
          case 'nested': {
            let nestedEntry = { key: currentKey, path: currentPath, value: pathOr(0, ['doc_count'], aggResult) };
            if ('groupings' in gSpec) {
              this.normalizeExAddGroupings(nestedEntry, aggResult, gSpec.groupings, currentPath, currentKey, record);
            }
            aggData = nestedEntry;
            break;
          }
          case 'sum': {
            aggData = Object.assign({ key: currentKey, path: currentPath }, aggResult);
            break;
          }
          case 'filter': {
            aggData = Object.assign({ key: currentKey, path: currentPath, value: aggResult?.doc_count || 0 }, aggResult);
            break;
          }
          case 'stats': {
            aggData = {
              key: currentKey,
              path: currentPath,
              value: {
                count: aggResult.count,
                min: aggResult.min,
                max: aggResult.max,
                avg: aggResult.avg,
                sum: aggResult.sum,
              },
            };
            break;
          }
        }
        const aggDataList = Array.isArray(aggData) ? aggData : [aggData];
        aggDataList.map((x) => {
          const rec = record ? record : x;
          if (rec && Array.isArray(rec.values)) {
            const val = {
              path: x.path,
              value: x.value,
            };
            if (rec.key !== x.key) {
              val.key = x.key;
            }
            rec.values.push(val);
          }
        });
        result[name] = aggData;
      }
    }
  }

  normalizeEx(elasticAggregations, groupings) {
    let result = {};
    this.normalizeExAddGroupings(result, elasticAggregations, groupings);
    return result;
  }

  toTable(normalized, rootPath, rootColumnTitle, columns) {
    const table = [];
    // This will accumulate total number of columns in table
    // First columns is root buckets
    let colCntr = 1;
    const tableColumns = {};
    const tableColumnsList = [];
    const entries = pathOr([], rootPath, normalized);
    // First pass - determine all columns
    for (let entry of entries) {
      entry.columns = {}; // Cache calculated columns here
      for (let val of entry.values) {
        const col = columns[val.path] || null;
        if (col) {
          const colId = val.path + ('key' in val ? `:${val.key}` : '');
          let colDef = tableColumns[colId] || null;
          if (!colDef) {
            // TODO pass in columns: titlePrefix, etc
            colDef = { title: val?.key || col?.title || val.path, idx: colCntr++, path: val.path, key: val?.key || null };
            tableColumns[colId] = colDef;
            tableColumnsList.push(colDef);
          }
          // Remember in columns in entry
          entry.columns[colId] = { value: val.value, idx: colDef.idx };
        }
      }
    }

    // Build table as array of arrays
    for (let entry of entries) {
      const row = new Array(colCntr).fill(0);
      // TODO Refine: This could be applicable only when we have buckets for rows - i.e. timeline
      row[0] = entry.key;
      Object.values(entry.columns).map((c) => {
        row[c.idx] = c.value;
      });
      table.push(row);
    }

    // Titles
    const titles = tableColumnsList.map((x) => x.title);
    titles.unshift(rootColumnTitle); // First column for root buckets

    return {
      tableHeader: titles,
      tableData: table,
    };
  }

  rotateTable(t) {
    const d = [];
    const tRows = Array.isArray(t) ? t.length : 0;
    const tCols = Array.isArray(t[0]) ? t[0].length : 0;
    if (tRows > 0 && tCols > 0) {
      for (let i = 0; i < tCols; i++) {
        const r = [];
        for (let j = 0; j < tRows; j++) {
          r.push(t[j][i]);
        }
        d.push(r);
      }
    }
    const header = d.shift();
    return {
      tableHeader: header,
      tableData: d,
    };
  }
}

let elasticTransforms = new ElasticTransforms();
export { elasticTransforms };
